import { Organization } from '@quadminds/client-apis/auth';
import { Button } from '@quadminds/ui-kit';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { SelectOrganizationComponentProps } from './SelectOrganizationComponent';
import defaultOrgPhoto from '../../assets/default-image-form.svg';

const DEFAULT_ORG_PHOTO = defaultOrgPhoto;
function OrganizationCard({
    organization,
    selectHandler,
    selectedOrganization,
    isSelectOrgLoading = false,
}: { organization: Organization } & Omit<SelectOrganizationComponentProps, 'organizations'>) {
    const [isHovered, setIsHovered] = useState(false);
    const { t } = useTranslation();

    const imgClasses = twMerge(
        'w-[146px] h-[146px] ring-[5px] rounded-full hover:shadow-sm mt-auto object-cover',
        isHovered ? 'ring-primary-100' : 'ring-primary-50',
    );

    const cardBgClasses = twMerge(
        'rounded-2xl flex-grow flex-shrink-0 basis-[calc(33%-1rem)] h-80 lg:h-[350px] flex flex-col items-center justify-center hover:shadow-sm',
        isHovered ? 'bg-primary-50 cursor-pointer' : 'bg-[#F2F3F9]',
    );

    return (
        <motion.div
            key={organization.id}
            className={cardBgClasses}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
                duration: 0.8,
                ease: [0, 0.71, 0.2, 1.01],
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => {
                selectHandler(organization.id);
            }}
        >
            <motion.div
                key={organization.id}
                data-test-id={`organization-${organization.id}-card`}
                className="flex flex-col items-center justify-center bg-white shadow-lg rounded-2xl h-[300px] p-3 w-[90%]"
            >
                <motion.img
                    className={imgClasses}
                    src={organization.photo ? organization.photo : DEFAULT_ORG_PHOTO}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        (e.target as HTMLImageElement).src = DEFAULT_ORG_PHOTO;
                    }}
                    data-test-id={`organization-${organization.id}-img`}
                    alt={organization.name}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                />

                <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    className="mt-4 text-center font-roboto text-base text-ellipsis truncate overflow-hidden text-gray-700 font-base w-full"
                >
                    {organization.name}
                </motion.span>
                <Button
                    size="lg"
                    loading={isSelectOrgLoading && organization.id === selectedOrganization}
                    dataTestId="organization-select-btn"
                    onClick={(e) => {
                        e.stopPropagation(); // Stop event propagation to prevent double triggering
                        selectHandler(organization.id);
                    }}
                    label={t('selectOrganizationComponent.btn')}
                    className="cursor-pointer w-full bg-primary mt-auto text-white rounded transition-colors duration-300 hover:bg-primary-400 focus:outline-none"
                ></Button>
            </motion.div>
        </motion.div>
    );
}

export default OrganizationCard;
